import { FormControl, FormLabel } from "@mui/material";
import { Controller } from "react-hook-form";
import { MuiTelInput, MuiTelInputInfo } from "mui-tel-input";

/** Sierra Leone local number length (excluding +232) */
const MAX_NATIONAL_DIGITS = 8;
const SL_CALLING_CODE = "232";

type AGPhoneInputProps = {
  label: string;
  required?: boolean;
  name: string;
  onChangeInput?: (value: string, info: MuiTelInputInfo) => void;
  value?: string;
  size?: "medium" | "small";
  error?: string;
  control: any; // Form control object from React Hook Form
  minLength?: number;
  maxLength?: number;
  onBlur?: () => void;
};

function getNationalDigits(val: string, info: MuiTelInputInfo): string {
  if (info.nationalNumber) {
    return info.nationalNumber.replace(/\D/g, "");
  }
  const escaped = SL_CALLING_CODE.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return val.replace(new RegExp(`^\\+?${escaped}`), "").replace(/\D/g, "");
}

function limitToMaxNationalDigits(
  val: string,
  info: MuiTelInputInfo,
): string {
  const national = getNationalDigits(val, info);
  if (national.length <= MAX_NATIONAL_DIGITS) {
    return val;
  }
  const truncated = national.slice(0, MAX_NATIONAL_DIGITS);
  return `+${info.countryCallingCode ?? SL_CALLING_CODE}${truncated}`;
}

function nationalDigitsFromValue(val: string): string {
  return val.replace(new RegExp(`^\\+?${SL_CALLING_CODE}`), "").replace(/\D/g, "");
}

const AGPhoneInput: React.FC<AGPhoneInputProps> = ({
  label,
  name,
  required = false,
  size = "small",
  value,
  onChangeInput,
  error,
  control,
  minLength,
  maxLength,
  onBlur,
}) => {
  return (
    <FormControl error={!!error} fullWidth margin="normal">
      <FormLabel className=" mb-1 font-semibold text-black text-sm">
        {label} {required ? "*" : ""}
      </FormLabel>
      <Controller
        name={name}
        control={control}
        defaultValue={value ?? ""}
        render={({ field }) => (
          <MuiTelInput
            error={!!error}
            helperText={error || ""}
            required={required}
            forceCallingCode
            onlyCountries={["SL"]}
            defaultCountry={"SL"}
            className="w-full"
            size={size}
            onBlur={onBlur}
            value={field.value}
            onChange={(val: string, info: MuiTelInputInfo) => {
              const limited = limitToMaxNationalDigits(val, info);
              field.onChange(limited);
              if (onChangeInput) onChangeInput(limited, info);
            }}
            name={name}
          />
        )}
        rules={
          required
            ? {
                validate: (val: string) => {
                  if (!val || val.trim() === "") {
                    return "Phone number cannot be empty";
                  }
                  const national = nationalDigitsFromValue(val);
                  if (national.length > MAX_NATIONAL_DIGITS) {
                    return `Phone number cannot exceed ${MAX_NATIONAL_DIGITS} digits (excluding country code)`;
                  }
                  if (national.length < MAX_NATIONAL_DIGITS) {
                    return `Phone number must be ${MAX_NATIONAL_DIGITS} digits (excluding country code)`;
                  }
                  if (minLength && val.length < minLength) {
                    return `Minimum length is ${minLength} characters`;
                  }
                  if (maxLength && val.length > maxLength) {
                    return `Maximum length is ${maxLength} characters`;
                  }
                  return true;
                },
              }
            : {
                validate: (val: string) => {
                  if (!val || val.trim() === "") return true;
                  const national = nationalDigitsFromValue(val);
                  if (national.length > MAX_NATIONAL_DIGITS) {
                    return `Phone number cannot exceed ${MAX_NATIONAL_DIGITS} digits (excluding country code)`;
                  }
                  return true;
                },
              }
        }
      />
    </FormControl>
  );
};

export default AGPhoneInput;
